import { createSelector } from 'reselect';
import isNil from 'lodash/isNil';
import { penceToPounds } from '$app/utils/util';
import { getVehicleOilCapacity, mapPriceListWorkOperation, quotePriceColumnsToPounds } from './util';

const globalState = (state) => state;
const bookingState = (state) => state.get('booking-page');
export const workDetailsState = (state) => state.get('workDetails');
export const quoteState = (state) => state.getIn(['workDetails', 'quote']);
export const campaignState = (state) => state.getIn(['booking-page', 'booking', 'campaign']);
export const proposedQuoteState = (state) => state.getIn(['booking-page', 'booking', 'proposedWork']);

export const garageBillStatusSelector = () =>
  createSelector(bookingState, bookingIdSelector, (booking, bookingId) => {
    const garageBills = booking.getIn(['booking', 'garageBills']);
    if (isNil(garageBills)) return '';
    const garageBill = garageBills.find(
      (bill) => bill.get('bookingId') === bookingId && bill.get('type') === 'bill'
    );
    const garageBillStatus = garageBill && garageBill.get('invoiceStatus');

    return garageBillStatus;
  });

export const getBookingSelector = () =>
  createSelector(bookingState, (state) => state.getIn(['booking']).toJS());

export const getBookingStatusSelector = () =>
  createSelector(bookingState, (state) => state.getIn(['booking', 'masterStatusId']));

export const isPopupOpen = () => createSelector(workDetailsState, (state) => state.getIn(['popupOpen']));

export const bookingCustomerIdSelector = (state) => state.getIn(['booking-page', 'booking', 'customerId']);

export const bookingVehicleIdSelector = (state) => state.getIn(['booking-page', 'booking', 'vehicleId']);

export const bookingQuoteSelector = (state) => state.getIn(['booking-page', 'booking', 'quote']).toJS();

export const bookingAcceptedGarageSelector = (state) => {
  const jobInvitationId = state.getIn(['booking-page', 'booking', 'jobInvitationId']);
  if (!jobInvitationId) return null;
  const acceptedInvite = state
    .getIn(['booking-page', 'booking', 'garageInvites'])
    .find((inv) => inv.get('invitationId') === jobInvitationId);
  if (!acceptedInvite) return null;
  return acceptedInvite.get('garage').toJS();
};

export const getBookingJobInvitationIdSelector = (state) =>
  state.getIn(['booking-page', 'booking', 'jobInvitationId']);

export const bookingMasterStatusIDSelector = (state) =>
  state.getIn(['booking-page', 'booking', 'masterStatusId']);

export const bookingIdSelector = (state) => state.getIn(['booking-page', 'booking', 'bookingId']);

export const bookingGarageStatusSelector = (state) =>
  state.getIn(['booking-page', 'booking', 'garageStatus']);

export const bookingGarageInviteSelector = (state) =>
  state.getIn(['booking-page', 'booking', 'garageInvites']).toJS();

export const jobInvitationSagaSelector = (state) => {
  const jobInvitation = state.getIn(['workDetails', 'jobInvitation']);
  return jobInvitation ? jobInvitation.toJS() : null;
};

export const quoteSelector = () =>
  createSelector(workDetailsState, (state) => {
    const quote = state.get('quote', null);
    if (!quote) return null;
    return quotePriceColumnsToPounds(quote.toJS());
  });

export const garageSelector = () =>
  createSelector(workDetailsState, (state) => {
    const garage = state.getIn(['garage']);
    return garage ? garage.toJS() : null;
  });

export const infoCardsSelector = () =>
  createSelector(workDetailsState, (state) => state.getIn(['infocards']).toJS());

export const proposedQuoteSelector = () => createSelector(proposedQuoteState, (state) => state.toJS());

export const proposedQuoteIdSelector = (state) => state.getIn(['booking-page', 'booking', 'proposedWorkId']);

const getVehicleFromBooking = (booking) => {
  if (!booking || typeof booking.getIn !== 'function') return {};
  const vehicle = booking.getIn(['booking', 'vehicle']);
  if (!vehicle) return {};
  return vehicle.toJS ? vehicle.toJS() : vehicle;
};

const toJsList = (value) => {
  if (!value) return [];
  if (typeof value.toJS === 'function') return value.toJS();
  return Array.isArray(value) ? value : [];
};

export const garageWorkOperationPricesSelector = () =>
  createSelector(workDetailsState, bookingState, (state, booking) => {
    const prices = toJsList(state.getIn(['prices', 'list']));
    const oilCapacity = getVehicleOilCapacity(getVehicleFromBooking(booking));

    const pricesList = prices.length > 0 ? prices.map((price) => mapPriceListWorkOperation(price, oilCapacity)) : [];

    return {
      list: pricesList,
      loading: state.getIn(['prices', 'loading'], false),
    };
  });

export const originalWorkOperationPricesSelector = () =>
  createSelector(workDetailsState, bookingState, (state, booking) => {
    const prices = toJsList(state.getIn(['originalPrices', 'list']));
    const oilCapacity = getVehicleOilCapacity(getVehicleFromBooking(booking));

    const pricesList = prices.length > 0 ? prices.map((price) => mapPriceListWorkOperation(price, oilCapacity)) : [];

    return {
      list: pricesList,
      loading: state.getIn(['originalPrices', 'loading'], false),
    };
  });

export const jobInvitationSelector = () =>
  createSelector(workDetailsState, (state) => {
    const inv = state.get('jobInvitation');
    return inv ? inv.toJS() : null;
  });

export const editingKeySelector = () =>
  createSelector(workDetailsState, (state) => state && state.get('editingKey'));

export const savingKeySelector = () => createSelector(workDetailsState, (state) => state.get('savingKey'));

export const deletingKeySelector = () =>
  createSelector(workDetailsState, (state) => state.get('deletingKey'));

export const isEditingRow = () => createSelector(workDetailsState, (state) => state.getIn(['editing']));

export const getCouponCodeSelector = () =>
  createSelector(bookingState, (state) => state.getIn(['booking', 'quote', 'campaign']));

export const getSelectedGaragePrice = () =>
  createSelector(
    globalState,
    (_, ownProps) => ownProps.garageOperations || {},
    (state, garageOperationsFromProps) => {
      const garageId = state.getIn(['workDetails', 'garage', 'garageId']);
      if (!garageId) return {};

      const garageOperations =
        garageOperationsFromProps[garageId] || state.getIn(['garageAllocation', 'garageOperations', garageId]);

      if (!garageOperations) return {};

      if (Array.isArray(garageOperations)) {
        return garageOperations.reduce((prices, operation) => {
          if (operation?.slug) prices[operation.slug] = penceToPounds(operation.price);
          return prices;
        }, {});
      }

      if (typeof garageOperations === 'object') {
        return Object.entries(garageOperations).reduce((prices, [key, value]) => {
          prices[key] = penceToPounds(value.price);
          return prices;
        }, {});
      }

      return garageOperations.map((op) => penceToPounds(op.get('price'))).toJS();
    }
  );

export const getVehicle = () =>
  createSelector(bookingState, (state) => state.getIn(['booking', 'vehicle']).toJS());

export const getAuthUser = () => createSelector(bookingState, (state) => state.get('authUser'));

export const getSendQuoteLoading = () =>
  createSelector(bookingState, (state) => state.get('sendQuoteLoading'));
